import React, { useState } from 'react';
import { Box, Button, InputBase, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { focusRing, meta, pillAction, pressable } from '../../../styles/visualLanguage';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';
import { stepPosition } from '../advisoryStepPosition';
import { refinementsRemaining } from '../advisoryPresentation';
import { normalizeControlCharacters } from '../../../redux/slices/advisory/advisoryTextSanitize';
import type { AdvisoryStage } from '../../../redux/slices/advisory/advisoryTypes';

/**
 * AdvisoryGoalReviewScreen
 *
 * Reads the current goal as a set-apart, read-only artifact, shows how many
 * refinements remain (derived, never a literal), lets the user send written
 * feedback that refines the goal, and confirms it. When refinement is
 * exhausted, distinct copy explains why and the refine affordance
 * disappears, but confirm remains — the document is still
 * `awaiting_confirmation` server-side and the server still accepts
 * `confirm: true`, so a strict dead end would strand the user on an
 * engagement the backend would happily proceed with.
 *
 * Receives an already-derived, narrowed `AdvisoryStage` and never touches
 * `goalDoc`, `errorCode` or the slice directly. `onRefine` and `onConfirm`
 * are minted by the caller — the same component instance that owns the
 * supervisor write-path hook — so this screen never calls that hook
 * itself, never stores the promise it returns, and never awaits it across
 * an unmount.
 *
 * No transcript, no version history, exactly one feedback field.
 * `GoalAttempt` carries no author field, so a transcript would come back
 * half-empty after a reload; only the current goal text is ever rendered.
 */
export interface AdvisoryGoalReviewScreenProps {
  stage: Extract<AdvisoryStage, { tag: 'awaitingConfirmation' | 'refinementExhausted' }>;
  goalText: string | null;
  onRefine: (feedback: string) => void;
  onConfirm: () => void;
}

export function AdvisoryGoalReviewScreen({
  stage,
  goalText,
  onRefine,
  onConfirm,
}: AdvisoryGoalReviewScreenProps) {
  const { t } = useUntypedTranslation();
  const [feedback, setFeedback] = useState('');

  // Goal review is counted step '02' and prints no denominator — a path
  // cannot exist before the goal is confirmed.
  const position = stepPosition('goal', null);

  const trimmedFeedback = feedback.trim();

  const handleRefine = () => {
    // Sanitize before the emptiness check and before onRefine is called —
    // a value that consists only of control characters must not fire
    // onRefine, even though `trimmedFeedback` above (used only
    // to drive the disabled-state binding and the Enter-key guard) would not
    // have caught it (control characters are not whitespace).
    const sanitized = normalizeControlCharacters(feedback);
    if (!sanitized) {
      return;
    }
    onRefine(sanitized);
    setFeedback('');
  };

  return (
    <AdvisoryEngagementShell
      position={position?.position ?? '02'}
      total={position?.total ?? null}
      eyebrowKey="advisory:goal.eyebrow"
      titleKey="advisory:goal.title"
      hintKey="advisory:goal.hint"
      actions={
        <>
          {stage.tag === 'awaitingConfirmation' ? (
            <Button
              variant="text"
              disableRipple
              disabled={trimmedFeedback.length === 0}
              onClick={handleRefine}
              sx={[pressable, focusRing]}
            >
              {t('advisory:goal.refineAction')}
            </Button>
          ) : null}
          <Button
            variant="contained"
            disableRipple
            onClick={onConfirm}
            sx={[pillAction, pressable, focusRing]}
          >
            {t('advisory:goal.confirmAction')}
          </Button>
        </>
      }
    >
      <Typography
        component="blockquote"
        data-testid="advisory-goal-readonly"
        aria-label={t('advisory:goal.readOnlyAriaLabel')}
        sx={theme => ({
          marginTop: theme.visualPrimitives.goalReadOnlyGap,
          marginLeft: 0,
          marginRight: 0,
          marginBottom: 0,
          fontSize: theme.visualPrimitives.goalReadOnlySize,
          lineHeight: theme.visualPrimitives.goalReadOnlyLineHeight,
          color: theme.colors.inkPrimary,
          borderLeft: `${theme.visualPrimitives.goalReadOnlyBorderWidth} solid ${theme.colors.lavender}`,
          padding: theme.visualPrimitives.goalReadOnlyPadding,
          maxWidth: theme.visualPrimitives.goalReadOnlyMaxWidth,
        })}
      >
        {goalText && goalText.trim() ? goalText : t('advisory:stage.titlePlaceholder')}
      </Typography>

      {stage.tag === 'awaitingConfirmation' ? (
        <Typography
          data-testid="advisory-refinements-remaining"
          sx={[meta, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
        >
          {t('advisory:goal.refinementsRemaining', { remaining: refinementsRemaining(stage) })}
        </Typography>
      ) : (
        <Typography
          data-testid="advisory-goal-exhausted"
          sx={[meta, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
        >
          {t('advisory:goal.exhaustedBody')}
        </Typography>
      )}

      {stage.tag === 'awaitingConfirmation' ? (
        <Box>
          <InputBase
            fullWidth
            data-testid="advisory-goal-feedback"
            inputProps={{ 'aria-label': t('advisory:goal.feedbackLabel') }}
            placeholder={t('advisory:goal.feedbackPlaceholder')}
            value={feedback}
            onChange={event => setFeedback(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && trimmedFeedback) {
                handleRefine();
              }
            }}
            sx={theme => ({
              fontSize: theme.visualPrimitives.inputSize,
              lineHeight: theme.visualPrimitives.inputLineHeight,
              marginTop: theme.visualPrimitives.inputGap,
              color: theme.colors.inkPrimary,
              borderBottom: `1px solid ${theme.visualPrimitives.border}`,
              '& .MuiInputBase-input': {
                padding: theme.visualPrimitives.inputPaddingY,
              },
              '& .MuiInputBase-input::placeholder': {
                color: theme.colors.inkTertiary,
                opacity: 1,
              },
              '&:focus-within': {
                borderBottomColor: theme.colors.lavenderDeep,
              },
            })}
          />
        </Box>
      ) : null}
    </AdvisoryEngagementShell>
  );
}

export default AdvisoryGoalReviewScreen;
